import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useBaseUrl } from '../../../utils/utils';
import * as localforage from 'localforage';
import Swal from 'sweetalert2';

interface AnalyticsData {
    summary: {
        total_resources: number;
        total_guidelines: number;
        total_notices: number;
        total_webinars: number;
        total_forum_topics: number;
        total_iot_devices: number;
    };
    access_metrics: {
        unique_resources_viewed_this_month: number;
        unique_resources_downloaded_this_month: number;
        total_views_this_month: number;
        total_downloads_this_month: number;
        weekly_views: number;
        weekly_downloads: number;
    };
    top_performers: {
        most_viewed: Array<{
            resource__title: string;
            resource__category__name: string;
            view_count: number;
        }>;
        most_downloaded: Array<{
            resource__title: string;
            resource__category__name: string;
            download_count: number;
        }>;
    };
    category_analytics: Array<{
        resource__category__name: string;
        views: number;
        downloads: number;
    }>;
    user_engagement: {
        active_users_this_month: number;
    };
}

interface UniqueMetrics {
    monthly: {
        unique_views: number;
        unique_downloads: number;
    };
    weekly: {
        unique_views: number;
        unique_downloads: number;
    };
    top_resources: {
        by_unique_views: Array<{
            resource__title: string;
            resource__category__name: string;
            unique_views: number;
        }>;
        by_unique_downloads: Array<{
            resource__title: string;
            resource__category__name: string;
            unique_downloads: number;
        }>;
    };
}

interface TrendsData {
    monthly_trends: Array<{
        month: string;
        views: number;
        downloads: number;
    }>;
    weekly_trends: Array<{
        week: string;
        views: number;
        downloads: number;
    }>;
}

interface Props {
    onBack: () => void;
}

const AnalyticsCenter: React.FC<Props> = ({ onBack }) => {
    const base_url = useBaseUrl();
    const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
    const [uniqueMetrics, setUniqueMetrics] = useState<UniqueMetrics | null>(null);
    const [trendsData, setTrendsData] = useState<TrendsData | null>(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'overview' | 'unique' | 'trends'>('overview');

    useEffect(() => {
        loadAnalyticsData();
    }, []);

    const loadAnalyticsData = async () => {
        try {
            setLoading(true);
            const clinicId = await localforage.getItem('clinic_id') as string;
            const staffId = await localforage.getItem('staff_id') as string;

            if (!clinicId || !staffId) {
                Swal.fire('Error', 'Authentication required', 'error');
                return;
            }

            const headers = {
                'Content-Type': 'application/json',
                'X-Clinic-ID': clinicId || '',
                'X-Staff-ID': staffId || '',
            };

            // Load dashboard analytics
            const dashboardResponse = await axios.get(
                `${base_url}resources/api/v1/analytics/dashboard/`,
                { headers }
            );

            // Load unique metrics
            const uniqueResponse = await axios.get(
                `${base_url}resources/api/v1/analytics/unique_metrics/`,
                { headers }
            );

            // Load trends
            const trendsResponse = await axios.get(
                `${base_url}resources/api/v1/analytics/trends/`,
                { headers }
            );

            setAnalyticsData(dashboardResponse.data);
            setUniqueMetrics(uniqueResponse.data);
            setTrendsData(trendsResponse.data);
        } catch (error) {
            console.error('Error loading analytics:', error);
            Swal.fire('Error', 'Failed to load analytics data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const formatNumber = (num: number) => {
        if (num >= 1000000) {
            return (num / 1000000).toFixed(1) + 'M';
        } else if (num >= 1000) {
            return (num / 1000).toFixed(1) + 'K';
        }
        return num.toString();
    };

    const getCategoryColor = (index: number) => {
        const colors = ['#007bff', '#28a745', '#ffc107', '#dc3545', '#6f42c1', '#fd7e14', '#20c997', '#e83e8c'];
        return colors[index % colors.length];
    };

    if (loading) {
        return (
            <div className="container-fluid p-4">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid p-4">
            {/* Header */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <button className="btn btn-outline-secondary me-3" onClick={onBack}>
                                <i className="bx bx-arrow-back me-1"></i>
                                Back to Resources
                            </button>
                            <h2 className="mb-0">
                                <i className="bx bx-bar-chart-alt-2 me-2 text-primary"></i>
                                Resources Analytics Center
                            </h2>
                        </div>
                        <div>
                            <button className="btn btn-primary" onClick={loadAnalyticsData}>
                                <i className="bx bx-refresh me-1"></i>
                                Refresh Data
                            </button>
                        </div>
                    </div>
                    <p className="text-muted mt-2">
                        Comprehensive analytics and insights for your resource center
                    </p>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="row mb-4">
                <div className="col-12">
                    <ul className="nav nav-tabs">
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                                onClick={() => setActiveTab('overview')}
                            >
                                <i className="bx bx-dashboard me-1"></i>
                                Overview
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'unique' ? 'active' : ''}`}
                                onClick={() => setActiveTab('unique')}
                            >
                                <i className="bx bx-target-lock me-1"></i>
                                Unique Metrics
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'trends' ? 'active' : ''}`}
                                onClick={() => setActiveTab('trends')}
                            >
                                <i className="bx bx-trending-up me-1"></i>
                                Trends
                            </button>
                        </li>
                    </ul>
                </div>
            </div>

            {/* Content based on active tab */}
            {activeTab === 'overview' && analyticsData && (
                <div className="row">
                    {/* Summary Cards */}
                    <div className="col-12 mb-4">
                        <h4 className="mb-3">Resource Summary</h4>
                        <div className="row">
                            <div className="col-md-2 col-sm-4 col-6 mb-3">
                                <div className="card text-center h-100">
                                    <div className="card-body">
                                        <i className="bx bx-file text-primary" style={{ fontSize: '2rem' }}></i>
                                        <h5 className="mt-2">{formatNumber(analyticsData.summary.total_resources)}</h5>
                                        <small className="text-muted">Total Resources</small>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-2 col-sm-4 col-6 mb-3">
                                <div className="card text-center h-100">
                                    <div className="card-body">
                                        <i className="bx bx-book-open text-success" style={{ fontSize: '2rem' }}></i>
                                        <h5 className="mt-2">{formatNumber(analyticsData.summary.total_guidelines)}</h5>
                                        <small className="text-muted">Guidelines</small>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-2 col-sm-4 col-6 mb-3">
                                <div className="card text-center h-100">
                                    <div className="card-body">
                                        <i className="bx bx-bell text-warning" style={{ fontSize: '2rem' }}></i>
                                        <h5 className="mt-2">{formatNumber(analyticsData.summary.total_notices)}</h5>
                                        <small className="text-muted">Notices</small>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-2 col-sm-4 col-6 mb-3">
                                <div className="card text-center h-100">
                                    <div className="card-body">
                                        <i className="bx bx-video text-info" style={{ fontSize: '2rem' }}></i>
                                        <h5 className="mt-2">{formatNumber(analyticsData.summary.total_webinars)}</h5>
                                        <small className="text-muted">Webinars</small>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-2 col-sm-4 col-6 mb-3">
                                <div className="card text-center h-100">
                                    <div className="card-body">
                                        <i className="bx bx-chat text-secondary" style={{ fontSize: '2rem' }}></i>
                                        <h5 className="mt-2">{formatNumber(analyticsData.summary.total_forum_topics)}</h5>
                                        <small className="text-muted">Forum Topics</small>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-2 col-sm-4 col-6 mb-3">
                                <div className="card text-center h-100">
                                    <div className="card-body">
                                        <i className="bx bx-chip text-danger" style={{ fontSize: '2rem' }}></i>
                                        <h5 className="mt-2">{formatNumber(analyticsData.summary.total_iot_devices)}</h5>
                                        <small className="text-muted">IoT Devices</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Access Metrics */}
                    <div className="col-12 mb-4">
                        <h4 className="mb-3">Access Metrics (This Month)</h4>
                        <div className="row">
                            <div className="col-md-3 mb-3">
                                <div className="card bg-primary text-white">
                                    <div className="card-body text-center">
                                        <i className="bx bx-show" style={{ fontSize: '2rem' }}></i>
                                        <h3 className="mt-2">{formatNumber(analyticsData.access_metrics.unique_resources_viewed_this_month)}</h3>
                                        <small>Unique Resources Viewed</small>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3 mb-3">
                                <div className="card bg-success text-white">
                                    <div className="card-body text-center">
                                        <i className="bx bx-download" style={{ fontSize: '2rem' }}></i>
                                        <h3 className="mt-2">{formatNumber(analyticsData.access_metrics.unique_resources_downloaded_this_month)}</h3>
                                        <small>Unique Resources Downloaded</small>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3 mb-3">
                                <div className="card bg-info text-white">
                                    <div className="card-body text-center">
                                        <i className="bx bx-trending-up" style={{ fontSize: '2rem' }}></i>
                                        <h3 className="mt-2">{formatNumber(analyticsData.access_metrics.total_views_this_month)}</h3>
                                        <small>Total Views</small>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3 mb-3">
                                <div className="card bg-warning text-dark">
                                    <div className="card-body text-center">
                                        <i className="bx bx-download" style={{ fontSize: '2rem' }}></i>
                                        <h3 className="mt-2">{formatNumber(analyticsData.access_metrics.total_downloads_this_month)}</h3>
                                        <small>Total Downloads</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Top Performers */}
                    <div className="col-md-6 mb-4">
                        <div className="card">
                            <div className="card-header">
                                <h5 className="mb-0">
                                    <i className="bx bx-show me-2 text-primary"></i>
                                    Most Viewed Resources
                                </h5>
                            </div>
                            <div className="card-body">
                                {analyticsData.top_performers.most_viewed.map((resource, index) => (
                                    <div key={index} className="d-flex justify-content-between align-items-center mb-2 p-2 border-bottom">
                                        <div>
                                            <strong>{resource.resource__title}</strong>
                                            <br />
                                            <small className="text-muted">{resource.resource__category__name}</small>
                                        </div>
                                        <span className="badge bg-primary">{resource.view_count} views</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="col-md-6 mb-4">
                        <div className="card">
                            <div className="card-header">
                                <h5 className="mb-0">
                                    <i className="bx bx-download me-2 text-success"></i>
                                    Most Downloaded Resources
                                </h5>
                            </div>
                            <div className="card-body">
                                {analyticsData.top_performers.most_downloaded.map((resource, index) => (
                                    <div key={index} className="d-flex justify-content-between align-items-center mb-2 p-2 border-bottom">
                                        <div>
                                            <strong>{resource.resource__title}</strong>
                                            <br />
                                            <small className="text-muted">{resource.resource__category__name}</small>
                                        </div>
                                        <span className="badge bg-success">{resource.download_count} downloads</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Category Performance */}
                    <div className="col-12 mb-4">
                        <div className="card">
                            <div className="card-header">
                                <h5 className="mb-0">
                                    <i className="bx bx-pie-chart me-2 text-info"></i>
                                    Category Performance
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    {analyticsData.category_analytics.map((category, index) => (
                                        <div key={index} className="col-md-4 mb-3">
                                            <div className="card border-0 shadow-sm">
                                                <div className="card-body">
                                                    <h6 className="card-title" style={{ color: getCategoryColor(index) }}>
                                                        {category.resource__category__name}
                                                    </h6>
                                                    <div className="row text-center">
                                                        <div className="col-6">
                                                            <div className="text-primary">
                                                                <strong>{formatNumber(category.views)}</strong>
                                                                <br />
                                                                <small>Views</small>
                                                            </div>
                                                        </div>
                                                        <div className="col-6">
                                                            <div className="text-success">
                                                                <strong>{formatNumber(category.downloads)}</strong>
                                                                <br />
                                                                <small>Downloads</small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'unique' && uniqueMetrics && (
                <div className="row">
                    <div className="col-12 mb-4">
                        <h4 className="mb-3">Unique Views & Downloads Metrics</h4>
                        <div className="row">
                            <div className="col-md-6 mb-4">
                                <div className="card">
                                    <div className="card-header">
                                        <h5 className="mb-0">Monthly Unique Metrics</h5>
                                    </div>
                                    <div className="card-body">
                                        <div className="row text-center">
                                            <div className="col-6">
                                                <div className="p-3">
                                                    <h3 className="text-primary">{formatNumber(uniqueMetrics.monthly.unique_views)}</h3>
                                                    <small className="text-muted">Unique Views</small>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="p-3">
                                                    <h3 className="text-success">{formatNumber(uniqueMetrics.monthly.unique_downloads)}</h3>
                                                    <small className="text-muted">Unique Downloads</small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6 mb-4">
                                <div className="card">
                                    <div className="card-header">
                                        <h5 className="mb-0">Weekly Unique Metrics</h5>
                                    </div>
                                    <div className="card-body">
                                        <div className="row text-center">
                                            <div className="col-6">
                                                <div className="p-3">
                                                    <h3 className="text-info">{formatNumber(uniqueMetrics.weekly.unique_views)}</h3>
                                                    <small className="text-muted">Unique Views</small>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="p-3">
                                                    <h3 className="text-warning">{formatNumber(uniqueMetrics.weekly.unique_downloads)}</h3>
                                                    <small className="text-muted">Unique Downloads</small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-6 mb-4">
                        <div className="card">
                            <div className="card-header">
                                <h5 className="mb-0">Top Resources by Unique Views</h5>
                            </div>
                            <div className="card-body">
                                {uniqueMetrics.top_resources.by_unique_views.map((resource, index) => (
                                    <div key={index} className="d-flex justify-content-between align-items-center mb-2 p-2 border-bottom">
                                        <div>
                                            <strong>{resource.resource__title}</strong>
                                            <br />
                                            <small className="text-muted">{resource.resource__category__name}</small>
                                        </div>
                                        <span className="badge bg-primary">{resource.unique_views} unique views</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="col-md-6 mb-4">
                        <div className="card">
                            <div className="card-header">
                                <h5 className="mb-0">Top Resources by Unique Downloads</h5>
                            </div>
                            <div className="card-body">
                                {uniqueMetrics.top_resources.by_unique_downloads.map((resource, index) => (
                                    <div key={index} className="d-flex justify-content-between align-items-center mb-2 p-2 border-bottom">
                                        <div>
                                            <strong>{resource.resource__title}</strong>
                                            <br />
                                            <small className="text-muted">{resource.resource__category__name}</small>
                                        </div>
                                        <span className="badge bg-success">{resource.unique_downloads} unique downloads</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'trends' && trendsData && (
                <div className="row">
                    <div className="col-12 mb-4">
                        <h4 className="mb-3">Trends Over Time</h4>
                    </div>

                    <div className="col-md-6 mb-4">
                        <div className="card">
                            <div className="card-header">
                                <h5 className="mb-0">Monthly Trends (Last 12 Months)</h5>
                            </div>
                            <div className="card-body">
                                {trendsData.monthly_trends.map((trend, index) => (
                                    <div key={index} className="mb-3">
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <strong>{trend.month}</strong>
                                        </div>
                                        <div className="progress mb-2" style={{ height: '8px' }}>
                                            <div
                                                className="progress-bar bg-primary"
                                                style={{ width: `${(trend.views / Math.max(...trendsData.monthly_trends.map(t => t.views))) * 100}%` }}
                                            ></div>
                                        </div>
                                        <div className="row text-center">
                                            <div className="col-6">
                                                <small className="text-primary">
                                                    <i className="bx bx-show me-1"></i>
                                                    {formatNumber(trend.views)} views
                                                </small>
                                            </div>
                                            <div className="col-6">
                                                <small className="text-success">
                                                    <i className="bx bx-download me-1"></i>
                                                    {formatNumber(trend.downloads)} downloads
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="col-md-6 mb-4">
                        <div className="card">
                            <div className="card-header">
                                <h5 className="mb-0">Weekly Trends (Last 4 Weeks)</h5>
                            </div>
                            <div className="card-body">
                                {trendsData.weekly_trends.map((trend, index) => (
                                    <div key={index} className="mb-3">
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <strong>{trend.week}</strong>
                                        </div>
                                        <div className="progress mb-2" style={{ height: '8px' }}>
                                            <div
                                                className="progress-bar bg-info"
                                                style={{ width: `${(trend.views / Math.max(...trendsData.weekly_trends.map(t => t.views))) * 100}%` }}
                                            ></div>
                                        </div>
                                        <div className="row text-center">
                                            <div className="col-6">
                                                <small className="text-info">
                                                    <i className="bx bx-show me-1"></i>
                                                    {formatNumber(trend.views)} views
                                                </small>
                                            </div>
                                            <div className="col-6">
                                                <small className="text-warning">
                                                    <i className="bx bx-download me-1"></i>
                                                    {formatNumber(trend.downloads)} downloads
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AnalyticsCenter;
